import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import { RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid, ComparisonTable, StepList, RichFAQ, RelatedLinks, RichCTA } from '../RichBlocks';
import RichPriceComparison from '../RichPriceComparison';
import RichEvBenefits from '../RichEvBenefits';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';
import V2Sections, { hasV2Content } from '../V2Sections';

/**
 * GuideRich — flexible bespoke template for the long tail of guide-style pages:
 * question_aeo, product_angle, life_situation, smart_charge_usp, data_authority,
 * ev_electricity, charger_comparison, calculator, topic_cluster.
 *
 * Generates clean hard-coded content from h1/intro/geography + the page's own
 * (clean) faqs, the enkel offer table, and the live price comparison when data
 * exists. Never renders the corrupted content_sections.
 */
export default function GuideRich({ page }) {
  const geo = page?.geography || {};
  const place = geo.name ? ` i ${geo.name}` : '';
  const zone = geo.zone;
  const year = page?.year || 2026;
  const p = page?.pricing_data || {};
  const evData = p?.evData || {};
  const hasComparison = Array.isArray(p?.topCompetitors) && p.topCompetitors.some((c) => c?.yearlyCost > 0);
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = (page?.faqs?.length ? page.faqs : [
    { question: 'Hvad koster el hos enkel energi?', answer: 'Spotpris + 0 øre tillæg og abonnement fra 18 kr./md. Ingen binding og intet depositum.' },
    { question: 'Hvordan skifter jeg elselskab?', answer: 'Vælg en el-aftale online (ca. 2 min). Vi opsiger automatisk hos dit nuværende selskab, og aktivering sker ofte samme dag.' },
    { question: 'Hvad er Saving Mode?', answer: 'Saving Mode lader din elbil automatisk i de billigste timer og kan spare op til ca. 30%. Inkluderet med alle el-aftaler.' },
  ]);

  // Use first FAQ as the AEO direct answer when it mirrors the H1 question.
  const lead = faqs[0];

  const v2 = hasV2Content(page);
  const toc = [
    { id: 'svar', label: 'Kort svar' },
    ...(v2
      ? page.content_sections.slice(0, 4).map((s, i) => ({ id: `sektion-${i + 1}`, label: s.heading.length > 34 ? s.heading.slice(0, 32) + '…' : s.heading }))
      : [{ id: 'enkel', label: 'enkels tilbud' }]),
    ...(hasComparison ? [{ id: 'priser', label: 'Prissammenligning' }] : []),
    { id: 'skift', label: 'Sådan skifter du' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Guides', path: '/elpris-sammenligning' }, { name: page?.h1 || 'Guide', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline={page?.h1} description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow={`Guide${zone ? ' · ' + zone : ''} · ${year}`}
        h1={page?.h1 || 'Guide til el og opladning'}
        intro={page?.intro_text}
        badges={['Spotpris + 0 øre tillæg', 'Fra 18 kr./md.', 'Ingen binding']}
        updated={updated}
        image="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '0 øre', label: 'tillæg til spotprisen' },
          { value: 'Fra 18 kr./md.', label: 'abonnement' },
          { value: '0 kr.', label: 'at skifte' },
          { value: '0 kr./md.', label: 'Saving Mode + service' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Kort svar">
        <AnswerBox question={lead?.question}>
          {lead?.answer}
        </AnswerBox>
        {place && zone && (
          <p>{geo.name} ligger i <strong>{zone}</strong>. Spotprisen er ens for alle leverandører i zonen – det er vilkårene oveni (tillæg, abonnement og services), der afgør din samlede pris.</p>
        )}
      </ProseSection>

      <V2Sections page={page} startAlt />

      {!v2 && <ProseSection id="enkel" heading="Det får du hos enkel energi" alt>
        <ComparisonTable
          caption="enkel energis tilbud"
          columns={[{ name: 'enkel energi', highlight: true }]}
          rows={[
            { label: 'Tillæg til spotpris', values: ['0 øre/kWh'] },
            { label: 'Abonnement', values: ['Fra 18 kr./md.'] },
            { label: 'Binding', values: [false] },
            { label: 'Saving Mode', values: ['0 kr./md.'] },
            { label: 'Laderabat', values: ['10 øre/kWh'] },
            { label: 'Serviceaftale (ladeboks)', values: ['0 kr./md.'] },
            { label: 'Elafgift-refusion', values: ['Automatisk'] },
          ]}
        />
        <p className="mt-4">For elbilister, der lader via Saving Mode i enkel-appen med kompatibel ladeboks og bil, kan laderabatten dække abonnementet – og giver ofte et overskud modregnet din regning. Tilsammen gør de inkluderede services den reelle pris pr. kWh markant lavere.</p>
      </ProseSection>}

      {hasComparison && (
        <RichPriceComparison
          enkelCost={p?.enkel?.yearlyCost || 216}
          consumption={p?.consumption || 9000}
          totalCompanies={p?.totalCompanies || 54}
          topCompetitors={p?.topCompetitors}
          timestamp={p?.timestamp}
          place={place}
        />
      )}

      {evData && Object.keys(evData).length > 0 && <RichEvBenefits evData={evData} />}

      <ProseSection id="skift" heading="Sådan skifter du elselskab" alt={!hasComparison}>
        <StepList steps={[
          { title: 'Vælg din el-aftale', body: 'Tager ca. 2 minutter online med MitID.' },
          { title: 'Vi opsiger det gamle', body: 'Vi håndterer opsigelsen hos dit nuværende selskab automatisk.' },
          { title: 'Aktivering', body: 'Ofte samme dag. Ingen afbrydelse, ingen depositum, ingen binding.' },
        ]} />
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/elpris-sammenligning', label: 'Sammenlign elselskaber' },
        { path: '/elaftale', label: 'Se el-aftaler' },
        { path: '/pris-time-for-time', label: 'Elpris time for time' },
        { path: '/opladere', label: 'Se ladebokse' },
        { path: '/portal/smart-charge', label: 'Sådan virker Saving Mode' },
        { path: '/el-fordele#laderabat', label: 'Laderabat – 10 øre/kWh' },
      ]} />

      <RichCTA />
      <SEODisclaimer page={page} />
    </article>
  );
}